import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ShieldOff, Clock, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * OrderControlStatusCard
 * ----------------------
 * Tiny dashboard widget that shows the live order-acceptance state.
 *
 * Two contexts:
 *   - "erp"   → calls /api/order-control/status (admin view of global state).
 *               ERP itself is never blocked, so we show "Stores Accepting"
 *               or "Stores Blocked" + the reason.
 *   - "store" → calls /api/store/order-control/status (franchise-aware).
 *               Shows whether THIS store can place orders right now.
 *
 * Polls every 60s so an admin's toggle is reflected without a manual refresh.
 */
const OrderControlStatusCard = ({ context = 'erp' }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const url = context === 'store'
      ? `${API}/store/order-control/status`
      : `${API}/order-control/status`;
    const fetcher = async () => {
      try {
        const token = localStorage.getItem(context === 'store' ? 'store_token' : 'token');
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (alive) setStatus(r.data);
      } catch {
        if (alive) setStatus({ accepting_orders: true, _silent: true });
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetcher();
    const t = setInterval(fetcher, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [context]);

  if (loading || (status && status._silent)) return null;

  // Resolve the "is accepting orders RIGHT NOW for this context?" boolean.
  const accepting = context === 'store'
    ? status?.accepting_orders
    : status?.store_accepting_orders; // ERP card shows global store state

  const reason = status?.reason;
  const message = status?.message;
  const reasonLabel = (() => {
    if (accepting) return null;
    if (reason === 'scheduled') return 'Scheduled Maintenance';
    if (reason === 'franchise_blocked') return 'Your franchise is blocked';
    if (reason === 'globally_blocked') return 'Globally Paused';
    return 'Orders Blocked';
  })();

  return (
    <div
      className={`rounded-xl border shadow-sm p-4 ${
        accepting
          ? 'bg-emerald-50 border-emerald-200'
          : 'bg-red-50 border-red-200'
      }`}
      data-testid={`order-status-card-${context}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${accepting ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {accepting ? (
            <ShieldCheck className="text-emerald-700" size={20} />
          ) : reason === 'scheduled' ? (
            <Clock className="text-red-700" size={20} />
          ) : reason === 'franchise_blocked' ? (
            <Lock className="text-red-700" size={20} />
          ) : (
            <ShieldOff className="text-red-700" size={20} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-xs uppercase tracking-wider font-semibold ${accepting ? 'text-emerald-700' : 'text-red-700'}`}>
            Order Acceptance
          </div>
          <div className={`text-lg font-bold ${accepting ? 'text-emerald-900' : 'text-red-900'}`}>
            {accepting ? (context === 'store' ? 'Orders Open' : 'Stores Accepting Orders')
                       : (context === 'store' ? 'Orders Blocked' : 'Stores Blocked')}
          </div>
          {!accepting && reasonLabel && (
            <div className="text-xs text-red-700 mt-0.5">{reasonLabel}</div>
          )}
          {!accepting && message && (
            <div className="text-xs text-slate-600 mt-2 line-clamp-3">{message}</div>
          )}
          {!accepting && status?.scheduled_until && (
            <div className="text-xs text-slate-500 mt-1">
              Resumes at {new Date(status.scheduled_until).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderControlStatusCard;
